import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, MapPin, Briefcase, GraduationCap, Heart, Home, Users, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ProfileCompletionPage = () => {
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loadUser } = useAuth();
  const [loading, setLoading] = useState(false);

  const [profileData, setProfileData] = useState({
    // Personal Details
    height: '',
    location: '',
    education: '',
    occupation: '',
    income: '',
    maritalStatus: 'Never Married',
    about: '',
    
    // Family Details
    familyStatus: '',
    familyType: '',
    fatherOccupation: '',
    motherOccupation: '',
    siblings: '',
    
    // Partner Preferences
    preferredAgeFrom: '',
    preferredAgeTo: '',
    preferredHeightFrom: '',
    preferredHeightTo: '',
    preferredMaritalStatus: 'Never Married',
    preferredReligion: '',
    preferredEducation: '',
    preferredOccupation: 'Any'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data for API
      const updateData = {
        firstName: profileData.firstName || user?.firstName,
        lastName: profileData.lastName || user?.lastName,
        height: profileData.height,
        location: profileData.location,
        education: profileData.education,
        occupation: profileData.occupation,
        income: profileData.income,
        maritalStatus: profileData.maritalStatus,
        about: profileData.about,
        familyDetails: {
          familyStatus: profileData.familyStatus,
          familyType: profileData.familyType,
          fatherOccupation: profileData.fatherOccupation,
          motherOccupation: profileData.motherOccupation,
          siblings: profileData.siblings
        },
        partnerPreferences: {
          ageRange: `${profileData.preferredAgeFrom}-${profileData.preferredAgeTo}`,
          heightRange: `${profileData.preferredHeightFrom} - ${profileData.preferredHeightTo}`,
          maritalStatus: profileData.preferredMaritalStatus,
          religion: profileData.preferredReligion,
          education: profileData.preferredEducation,
          occupation: profileData.preferredOccupation
        }
      };

      // If profile image is uploaded, add it to updateData
      if (profileData.imagePreview) {
        updateData.image = profileData.imagePreview;
      }

      await userAPI.updateProfile(updateData);
      await loadUser();

      toast({
        title: "Profile Completed!",
        description: "Alhamdulillah! Your profile is now complete. Start finding your match!",
      });

      setTimeout(() => {
        navigate('/search');
      }, 1500);
    } catch (error) {
      console.error('Profile update error:', error);
      toast({
        title: "Update Failed",
        description: error.response?.data?.detail || "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const goToNextStep = () => {
    if (step < 3) setStep(step + 1);
  };

  const goToPreviousStep = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Profile</h1>
          <p className="text-gray-600">Help others find you by providing more details</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            <div className={`flex items-center ${step >= 1 ? 'text-navy-500' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                step >= 1 ? 'border-mint-500 bg-mint-500 text-white' : 'border-gray-300'
              }`}>
                {step > 1 ? <Check className="w-6 h-6" /> : '1'}
              </div>
              <span className="ml-2 font-medium hidden sm:inline">Personal</span>
            </div>
            <div className={`w-16 sm:w-24 h-1 mx-2 ${step >= 2 ? 'bg-mint-500' : 'bg-gray-300'}`}></div>
            <div className={`flex items-center ${step >= 2 ? 'text-navy-500' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                step >= 2 ? 'border-mint-500 bg-mint-500 text-white' : 'border-gray-300'
              }`}>
                {step > 2 ? <Check className="w-6 h-6" /> : '2'}
              </div>
              <span className="ml-2 font-medium hidden sm:inline">Family</span>
            </div>
            <div className={`w-16 sm:w-24 h-1 mx-2 ${step >= 3 ? 'bg-mint-500' : 'bg-gray-300'}`}></div>
            <div className={`flex items-center ${step >= 3 ? 'text-navy-500' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                step >= 3 ? 'border-mint-500 bg-mint-500 text-white' : 'border-gray-300'
              }`}>
                3
              </div>
              <span className="ml-2 font-medium hidden sm:inline">Preferences</span>
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit}>
              {/* Step 1: Personal Details */}
              {step === 1 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-semibold mb-4 flex items-center">
                    <User className="w-6 h-6 mr-2 text-navy-500" />
                    Personal Details
                  </h2>

                  {/* Profile Picture Upload */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Profile Picture (Optional)</label>
                    <div className="flex flex-col items-center gap-4">
                      <div className="relative">
                        <img
                          src={profileData.imagePreview || 'https://via.placeholder.com/150'}
                          alt="Profile"
                          className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                        />
                      </div>
                      <div className="text-center">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              if (file.size > 5 * 1024 * 1024) {
                                toast({
                                  title: "Error",
                                  description: "Image size should be less than 5MB",
                                  variant: "destructive",
                                });
                                return;
                              }
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setProfileData({...profileData, profileImage: file, imagePreview: reader.result});
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="hidden"
                          id="profile-picture-completion"
                        />
                        <label
                          htmlFor="profile-picture-completion"
                          className="cursor-pointer bg-mint-500 text-white px-6 py-2 rounded-lg hover:bg-mint-600 transition-colors inline-block"
                        >
                          Upload Picture
                        </label>
                        <p className="text-sm text-gray-500 mt-2">JPG, PNG or JPEG. Max size 5MB</p>
                      </div>
                    </div>
                  </div>

                  {/* Name Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                      <Input
                        type="text"
                        value={profileData.firstName || user?.firstName || ''}
                        onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
                        placeholder="Enter first name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                      <Input
                        type="text"
                        value={profileData.lastName || user?.lastName || ''}
                        onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
                        placeholder="Enter last name (optional)"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Height *</label>
                      <Select value={profileData.height} onValueChange={(value) => setProfileData({...profileData, height: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select height" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="4ft10in">4&apos;10&quot;</SelectItem>
                          <SelectItem value="5ft0in">5&apos;0&quot;</SelectItem>
                          <SelectItem value="5ft2in">5&apos;2&quot;</SelectItem>
                          <SelectItem value="5ft4in">5&apos;4&quot;</SelectItem>
                          <SelectItem value="5ft6in">5&apos;6&quot;</SelectItem>
                          <SelectItem value="5ft8in">5&apos;8&quot;</SelectItem>
                          <SelectItem value="5ft10in">5&apos;10&quot;</SelectItem>
                          <SelectItem value="6ft0in">6&apos;0&quot;</SelectItem>
                          <SelectItem value="6ft2in">6&apos;2&quot;</SelectItem>
                          <SelectItem value="6ft4in">6&apos;4&quot;</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Marital Status *</label>
                      <Select value={profileData.maritalStatus} onValueChange={(value) => setProfileData({...profileData, maritalStatus: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Never Married">Never Married</SelectItem>
                          <SelectItem value="Divorced">Divorced</SelectItem>
                          <SelectItem value="Widowed">Widowed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="w-4 h-4 inline mr-1" />Location (City, Province) *
                    </label>
                    <Input
                      placeholder="e.g., Toronto, Ontario"
                      value={profileData.location}
                      onChange={(e) => setProfileData({...profileData, location: e.target.value})}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <GraduationCap className="w-4 h-4 inline mr-1" />Education *
                      </label>
                      <Select value={profileData.education} onValueChange={(value) => setProfileData({...profileData, education: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select education" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="High School">High School</SelectItem>
                          <SelectItem value="Diploma">Diploma</SelectItem>
                          <SelectItem value="Bachelor's">Bachelor's</SelectItem>
                          <SelectItem value="Master's">Master's</SelectItem>
                          <SelectItem value="PhD">PhD</SelectItem>
                          <SelectItem value="Professional Degree">Professional Degree</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Briefcase className="w-4 h-4 inline mr-1" />Occupation *
                      </label>
                      <Input
                        placeholder="e.g., Software Engineer"
                        value={profileData.occupation}
                        onChange={(e) => setProfileData({...profileData, occupation: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Annual Income (CAD)</label>
                    <Select value={profileData.income} onValueChange={(value) => setProfileData({...profileData, income: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select income range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Under 30K">Under 30K</SelectItem>
                        <SelectItem value="30-50K">30-50K</SelectItem>
                        <SelectItem value="50-70K">50-70K</SelectItem>
                        <SelectItem value="70-90K">70-90K</SelectItem>
                        <SelectItem value="90-120K">90-120K</SelectItem>
                        <SelectItem value="120K+">120K+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">About Yourself *</label>
                    <Textarea
                      placeholder="Tell others about yourself, your values, and what you're looking for..."
                      value={profileData.about}
                      onChange={(e) => setProfileData({...profileData, about: e.target.value})}
                      rows={4}
                      required
                    />
                  </div>

                  <Button
                    type="button"
                    onClick={goToNextStep}
                    className="w-full bg-mint-500 hover:bg-mint-600 text-white py-6"
                  >
                    Next: Family Details <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              )}

              {/* Step 2: Family Details */}
              {step === 2 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-semibold mb-4 flex items-center">
                    <Users className="w-6 h-6 mr-2 text-navy-500" />
                    Family Background
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Family Status *</label>
                      <Select value={profileData.familyStatus} onValueChange={(value) => setProfileData({...profileData, familyStatus: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Middle Class">Middle Class</SelectItem>
                          <SelectItem value="Upper Middle Class">Upper Middle Class</SelectItem>
                          <SelectItem value="Affluent">Affluent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Family Type *</label>
                      <Select value={profileData.familyType} onValueChange={(value) => setProfileData({...profileData, familyType: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Nuclear Family">Nuclear Family</SelectItem>
                          <SelectItem value="Joint Family">Joint Family</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Father's Occupation</label>
                      <Input
                        placeholder="e.g., Engineer"
                        value={profileData.fatherOccupation}
                        onChange={(e) => setProfileData({...profileData, fatherOccupation: e.target.value})}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Mother's Occupation</label>
                      <Input
                        placeholder="e.g., Teacher"
                        value={profileData.motherOccupation}
                        onChange={(e) => setProfileData({...profileData, motherOccupation: e.target.value})}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Siblings</label>
                    <Input
                      placeholder="e.g., 1 Brother, 2 Sisters"
                      value={profileData.siblings}
                      onChange={(e) => setProfileData({...profileData, siblings: e.target.value})}
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      onClick={goToPreviousStep}
                      variant="outline"
                      className="flex-1 py-6"
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={goToNextStep}
                      className="flex-1 bg-mint-500 hover:bg-mint-600 text-white py-6"
                    >
                      Next: Partner Preferences <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Partner Preferences */}
              {step === 3 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-semibold mb-4 flex items-center">
                    <Heart className="w-6 h-6 mr-2 text-navy-500" />
                    Partner Preferences
                  </h2>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Age Range *</label>
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        type="number"
                        placeholder="From"
                        value={profileData.preferredAgeFrom}
                        onChange={(e) => setProfileData({...profileData, preferredAgeFrom: e.target.value})}
                        required
                      />
                      <Input
                        type="number"
                        placeholder="To"
                        value={profileData.preferredAgeTo}
                        onChange={(e) => setProfileData({...profileData, preferredAgeTo: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Height Range</label>
                    <div className="grid grid-cols-2 gap-4">
                      <Select value={profileData.preferredHeightFrom} onValueChange={(value) => setProfileData({...profileData, preferredHeightFrom: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="From" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="4ft10in">4&apos;10&quot;</SelectItem>
                          <SelectItem value="5ft0in">5&apos;0&quot;</SelectItem>
                          <SelectItem value="5ft4in">5&apos;4&quot;</SelectItem>
                          <SelectItem value="5ft8in">5&apos;8&quot;</SelectItem>
                          <SelectItem value="6ft0in">6&apos;0&quot;</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={profileData.preferredHeightTo} onValueChange={(value) => setProfileData({...profileData, preferredHeightTo: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="To" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5ft4in">5&apos;4&quot;</SelectItem>
                          <SelectItem value="5ft8in">5&apos;8&quot;</SelectItem>
                          <SelectItem value="6ft0in">6&apos;0&quot;</SelectItem>
                          <SelectItem value="6ft4in">6&apos;4&quot;</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Marital Status</label>
                      <Select value={profileData.preferredMaritalStatus} onValueChange={(value) => setProfileData({...profileData, preferredMaritalStatus: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Never Married">Never Married</SelectItem>
                          <SelectItem value="Divorced">Divorced</SelectItem>
                          <SelectItem value="Widowed">Widowed</SelectItem>
                          <SelectItem value="Any">Any</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Islamic Sect</label>
                      <Select value={profileData.preferredReligion} onValueChange={(value) => setProfileData({...profileData, preferredReligion: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Sunni">Sunni</SelectItem>
                          <SelectItem value="Shia">Shia</SelectItem>
                          <SelectItem value="Ahmadi">Ahmadi</SelectItem>
                          <SelectItem value="Sufi">Sufi</SelectItem>
                          <SelectItem value="Any">Any</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Education</label>
                    <Select value={profileData.preferredEducation} onValueChange={(value) => setProfileData({...profileData, preferredEducation: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="High School or above">High School or above</SelectItem>
                        <SelectItem value="Bachelor's or above">Bachelor's or above</SelectItem>
                        <SelectItem value="Master's or above">Master's or above</SelectItem>
                        <SelectItem value="Any">Any</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      onClick={goToPreviousStep}
                      variant="outline"
                      className="flex-1 py-6"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-mint-500 hover:bg-mint-600 text-white py-6"
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Complete Profile'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-600 mt-6">
          You can always update these details later from your profile settings
        </p>
      </div>
    </div>
  );
};

export default ProfileCompletionPage;